/* Before/after compare — dragged by the visitor, not by page scroll.

   The split position is published as the --rcfa-split custom property so the
   clip-path, the divider and the handle all read one value and the visual
   rules stay in CSS. Drag is captured on the handle rather than the whole
   photo: the card fills the viewport, so swallowing touch across it would
   trap the page scroll. */

(() => {
  const STEP = 2;
  const PAGE_STEP = 10;
  const MIN = 0;
  const MAX = 100;

  const clamp = (value) => Math.max(MIN, Math.min(MAX, value));

  class RcfaCompare extends HTMLElement {
    connectedCallback() {
      this.handle = this.querySelector('[data-rcfa-compare-handle]');
      if (!this.handle) return;

      this.dragging = false;
      this.split = clamp(Number(this.dataset.start) || 50);

      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);

      this.handle.addEventListener('pointerdown', this.handlePointerDown);
      this.handle.addEventListener('pointermove', this.handlePointerMove);
      this.handle.addEventListener('pointerup', this.handlePointerUp);
      this.handle.addEventListener('pointercancel', this.handlePointerUp);
      this.handle.addEventListener('keydown', this.handleKeydown);
    }

    disconnectedCallback() {
      if (!this.handle) return;

      this.handle.removeEventListener('pointerdown', this.handlePointerDown);
      this.handle.removeEventListener('pointermove', this.handlePointerMove);
      this.handle.removeEventListener('pointerup', this.handlePointerUp);
      this.handle.removeEventListener('pointercancel', this.handlePointerUp);
      this.handle.removeEventListener('keydown', this.handleKeydown);
    }

    handlePointerDown(event) {
      this.dragging = true;
      // capture keeps the moves coming to the handle even when the pointer
      // runs past the edge of the photo
      this.handle.setPointerCapture(event.pointerId);
      this.classList.add('is-dragging', 'is-interacted');
    }

    handlePointerMove(event) {
      if (!this.dragging) return;

      event.preventDefault();
      const { left, width } = this.getBoundingClientRect();
      if (!width) return;

      this.setSplit(((event.clientX - left) / width) * 100);
    }

    handlePointerUp(event) {
      if (!this.dragging) return;

      this.dragging = false;
      this.handle.releasePointerCapture?.(event.pointerId);
      this.classList.remove('is-dragging');
    }

    handleKeydown(event) {
      const offsets = {
        ArrowLeft: -STEP,
        ArrowRight: STEP,
        PageDown: -PAGE_STEP,
        PageUp: PAGE_STEP,
      };

      let next = null;
      if (event.key in offsets) next = this.split + offsets[event.key];
      if (event.key === 'Home') next = MIN;
      if (event.key === 'End') next = MAX;
      if (next === null) return;

      event.preventDefault();
      this.classList.add('is-interacted');
      this.setSplit(next);
    }

    setSplit(value) {
      this.split = clamp(value);
      // a continuous position, so it belongs in a custom property rather than
      // a class — every visual rule that consumes it lives in the stylesheet
      this.style.setProperty('--rcfa-split', `${this.split}%`);
      this.handle.setAttribute('aria-valuenow', String(Math.round(this.split)));
    }
  }

  if (!customElements.get('rcfa-compare')) {
    customElements.define('rcfa-compare', RcfaCompare);
  }
})();
